import { useEffect, useRef } from "react";
import { useAui } from "@assistant-ui/react";
import { useAppSelector } from "../store/hooks";

// Per-session composer drafts. Ports the legacy Chatbot.tsx behavior:
// type a partial message in Chat A → switch to Chat B → return to A →
// the draft is still there. assistant-ui's composer is otherwise
// uncontrolled, so we drive it imperatively via `aui.composer().setText()`
// at the moment of session change.
//
// The drafts map lives in a ref so it survives Redux updates but resets
// on page unmount — same lifetime as the legacy local-state map.
// Mount this component anywhere inside the AuiProvider; it renders
// nothing and only runs side-effects on session change.
export function DraftSync() {
  const aui = useAui();
  const sessionId = useAppSelector((s) => s.chat.currentSession?.id ?? "");
  const draftsRef = useRef<Record<string, string>>({});
  const prevSessionIdRef = useRef<string>(sessionId);

  useEffect(() => {
    if (prevSessionIdRef.current === sessionId) return;
    const prevId = prevSessionIdRef.current;

    // Save the text we're leaving behind. `getState()` is a one-shot
    // read of the latest composer state — no reactive subscription
    // needed here, we just need the value at this exact instant.
    const composer = aui.composer();
    const currentText = composer.getState().text ?? "";
    draftsRef.current[prevId] = currentText;

    // Restore the new session's draft (or clear if none).
    composer.setText(draftsRef.current[sessionId] ?? "");
    prevSessionIdRef.current = sessionId;

    // Refocus the composer input so the user can keep typing right
    // away — matches the legacy Chatbot.tsx behavior. assistant-ui's
    // composer runtime has no focus() method, so we reach into the
    // DOM via the aria-label set on ComposerPrimitive.Input.
    // setTimeout 0 lets React commit the new draft first.
    setTimeout(() => {
      const el = document.querySelector<HTMLTextAreaElement>(
        '[aria-label="Message input"]',
      );
      el?.focus();
    }, 0);
  }, [sessionId, aui]);

  return null;
}

export default DraftSync;
